import HttpFeedbackMap from "./HttpFeedbackMap";
import RequestFeedback from "./ResponseFeedback";

type RestResponse<T> = Promise<RestSuccessResponse<T> | RestErrorResponse>;

/**
 * A failed request thrown as an error, keeping the HTTP status a plain `Error` would lose. Query and
 * mutation functions that throw this let the retry policy tell a settled 4xx from a transient
 * failure — see `lib/query/retry.ts` (LAR-456).
 */
export class RestError extends Error {
  readonly status: number;
  /** Set only on a synthetic 401 the client produced — see {@link ClientAuthFailure}. */
  readonly clientAuthFailure?: ClientAuthFailure;

  constructor(
    status: number,
    message: string,
    clientAuthFailure?: ClientAuthFailure
  ) {
    super(message);
    this.name = "RestError";
    this.status = status;
    this.clientAuthFailure = clientAuthFailure;
  }
}

/**
 * Turns a failed response into the error a query or mutation function throws. Throw this rather
 * than `new Error(response.feedback.message)`: that plain `Error` drops the status, so the retry
 * policy retries a 404 three times and the query log cannot say what failed (LAR-558). `message`
 * overrides the feedback text for callers that match on their own wording.
 */
export const restError = (
  response: RestErrorResponse,
  message: string = response.feedback.message
): RestError =>
  new RestError(response.status, message, response.clientAuthFailure);

export type RestSuccessResponse<T> = {
  status: number;
  statusText?: string;
  success: true;
  feedback: RequestFeedback;
  value: T;
};

/**
 * Why a 401 was produced client-side, without the request ever reaching the API:
 * - `token-unavailable`: acquiring an access token failed (silent refresh dead,
 *   or the interactive-login circuit-breaker is latched).
 * - `not-authenticated`: the Auth0 SDK had already dropped `isAuthenticated`.
 *
 * Both are recoverable app state, not the API rejecting the user, so callers must
 * not treat them as a dead session (LAR-361).
 */
export type ClientAuthFailure = "token-unavailable" | "not-authenticated";

export type RestErrorResponse = {
  status: number;
  statusText?: string;
  success: false;
  feedback: RequestFeedback;
  /** Set only on a synthetic 401 — see {@link ClientAuthFailure}. */
  clientAuthFailure?: ClientAuthFailure;
};

export type ErrorResponseJson = {
  statusCode?: string;
  errorCode?: string;
  severity?: RequestSeverity;
  message?: string;
  stacktrace?: string;
  /**
   * Set by the API on rejections it phrased for the user (a violated data rule, a
   * missing entity, a forbidden action). Absent on framework-generated problems,
   * whose `title` is generic English — hence a dedicated field rather than reading
   * `title`, so a 500 keeps the app's own wording (LAR-408).
   */
  localizedMessage?: string;
  /** RFC7807 fields the API sends alongside; technical, never shown to the user. */
  title?: string;
  detail?: string;
  type?: string;
};

export type RequestSeverity = "success" | "info" | "error" | "warning";

/** Last resort when the status is not one {@link HttpFeedbackMap} knows. */
const UNKNOWN_STATUS_FEEDBACK: RequestFeedback = {
  severity: "error",
  errorCode: "client/unknown-error",
  message: "Der opstod en uventet fejl",
};

/**
 * Feedback for a failed request. The API's own wording wins when it sent some —
 * `localizedMessage`, or the legacy `message` body — otherwise the app's per-status
 * text stands. Only those two fields are read: the rest of an RFC7807 body is
 * technical and must not reach the user.
 */
const feedbackFromError = (
  status: number,
  errorResponse?: ErrorResponseJson | string
): RequestFeedback => {
  const base = HttpFeedbackMap[status] ?? UNKNOWN_STATUS_FEEDBACK;

  if (typeof errorResponse === "string") {
    return errorResponse.trim().length > 0
      ? { ...base, message: errorResponse }
      : base;
  }

  if (!errorResponse) return base;

  const apiMessage = [errorResponse.localizedMessage, errorResponse.message]
    .map((message) => message?.trim())
    .find((message) => message && message.length > 0);

  return {
    ...base,
    ...(errorResponse.severity ? { severity: errorResponse.severity } : {}),
    ...(errorResponse.errorCode ? { errorCode: errorResponse.errorCode } : {}),
    ...(errorResponse.stacktrace
      ? { stacktrace: errorResponse.stacktrace }
      : {}),
    ...(apiMessage ? { message: apiMessage } : {}),
  };
};

export const responseFromError = (
  status: number,
  errorResponse?: ErrorResponseJson | string
): RestErrorResponse => ({
  status: status,
  success: false,
  feedback: feedbackFromError(status, errorResponse),
});

/** A 401 the client manufactured because it could not send the request at all. */
export const responseFromClientAuthFailure = (
  clientAuthFailure: ClientAuthFailure,
  message: string
): RestErrorResponse => ({
  ...responseFromError(401, message),
  clientAuthFailure,
});

/**
 * True when a failed response is our own synthetic 401 rather than the API
 * rejecting the caller. Handlers that sign the user out or navigate away must
 * check this first (LAR-361).
 */
export const isClientAuthFailure = (response: RestErrorResponse): boolean =>
  response.clientAuthFailure !== undefined;

export default RestResponse;
